import { NotificationStatuses } from 'constants/notifications';

import CircleIcon from '@mui/icons-material/Circle';
import ReportIcon from '@mui/icons-material/Report';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Box } from 'shared/mui/Box';
import { ListItemAvatar, ListItemText } from 'shared/mui/List';
import { Tooltip } from 'shared/mui/Tooltip';
import { NotificationPopperListItem } from 'shared/widgets/Notification/NotificationPopper/NotificationPopperListItem/NotificationPopperListItem';
import {
  Notification,
  NotificationBody,
  NotificationBodyContent,
} from 'types/embedded/notification';

import {
  CircularProgressStyled,
  DownloadIconStyled,
  FailureIconButtonStyled,
  RadioButtonUncheckedIconStyled,
  SuccessIconButtonStyled,
} from './NotificationPopperListItemFile.styled';
import { NotificationPopperListItemFileSecondary } from './NotificationPopperListItemFileSecondary';

type Props = {
  notification: Notification<
    NotificationBody<Partial<Pick<NotificationBodyContent, 'fileExtension'>>>
  >;
  createdAt: number;
  primary: React.ReactNode;
  isDownloading?: boolean;
  onDownload: VoidFunction;
  dataId: string;
};

export const NotificationPopperListItemFile: React.FC<Props> = ({
  notification,
  createdAt,
  primary,
  isDownloading,
  onDownload,
  dataId,
}) => {
  const translate = useTypedTranslate();
  const { status, content } = notification.body;
  const normalizedStatus = notification.isDownloaded
    ? NotificationStatuses.Downloaded
    : (status as NotificationStatuses);
  const isSuccess =
    normalizedStatus === NotificationStatuses.Done ||
    normalizedStatus === NotificationStatuses.Downloaded;
  const isFailure = normalizedStatus === NotificationStatuses.Failure;

  return (
    <NotificationPopperListItem data-id={dataId}>
      <ListItemAvatar>
        <>
          {isSuccess && (
            <Tooltip title={translate('app.notificationPopper.download')}>
              <span>
                <SuccessIconButtonStyled disabled={isDownloading} onClick={onDownload}>
                  {isDownloading ? <CircularProgressStyled size={20} /> : <DownloadIconStyled />}
                </SuccessIconButtonStyled>
              </span>
            </Tooltip>
          )}
          {isFailure && (
            <FailureIconButtonStyled>
              <ReportIcon color="error" />
            </FailureIconButtonStyled>
          )}
        </>
      </ListItemAvatar>
      <ListItemText
        primary={primary}
        primaryTypographyProps={{ component: 'div', sx: { marginBottom: '8px' } }}
        secondary={
          <NotificationPopperListItemFileSecondary
            createdAt={createdAt}
            fileExtension={content.fileExtension}
            isFailure={isFailure}
            isSuccess={isSuccess}
            status={normalizedStatus}
          />
        }
        secondaryTypographyProps={{ component: 'div' }}
        sx={{ margin: 0 }}
      />
      <Box sx={{ marginTop: '2px' }}>
        {notification.isViewed ? (
          <RadioButtonUncheckedIconStyled sx={{ fontSize: '8px' }} />
        ) : (
          <CircleIcon color="primary" sx={{ fontSize: '8px' }} />
        )}
      </Box>
    </NotificationPopperListItem>
  );
};
